import CustomButton from "components/button/CustomButton";
import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import httpClient from "services/network/httpClient";
import { epochToDate } from "utility/dateFormat";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarAlt, faDownload, faEdit, faFilter, faLeaf, faSeedling, faUser } from "@fortawesome/free-solid-svg-icons";
import ActionsCell from "pages/dashboard/completeDemo/ActionsCell";
import DataTable from "components/DataTable";
import GenericModal from "components/Modal";
import ViewDetails from "pages/dashboard/completeDemo/ViewDetails";
import { motion } from "framer-motion";
import DemoReportFilter, { TotalSummaryFormProps, FilterFormValues } from "./demo/DemoReportFilter";
import axios from "axios";
import { getDefaultDateRange } from "pages/report/ReportList";
import FilterBadge, { FilterChip } from "components/filter/FilterBadge";
import appConfig from "services/config";
import { getFileNameFromDisposition } from "./VisitReport";
import DateTimeCell from "components/DataTable/DateTimeCell";
import FollowUpDateTimeCell from "components/DataTable/FolloUpDateTimeCell";
// Utility: ms (number|string) → 'YYYY-MM-DD'
// function msToDateInput(ms: number | string | undefined): string {
//     if (!ms) return '';
//     const d = new Date(Number(ms));
//     return d.toISOString().slice(0, 10);
// }
export const msToDateInput = (ms: number | string | undefined): string => {
    if (!ms) return '';

    const d = new Date(Number(ms));

    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
};

export interface VisitData {
    id: number;
    farmerName: string;
    farmerContactNumber: string;
    houseNumber: string;
    village: string;
    district: string;
    pinCode: string;
    state: string;
    tehsilMandal: string;
    latitude: string;
    longitude: string;
    createdDate: number;
    productName: string;
    cropName: string;
    cultivatedAcres: string;
    fromSeedingPeriod: string;
    toSeedingPeriod: string;
    fromHarvestingPeriod: string;
    toHarvestingPeriod: string;
    url: string;
    notes: string;
    purchaseQuantity: number;
    nextFollowUpDate?: number;
    employeeName?: string
    employeeCode?: string
    managerName?: string
    managerCode?: string
}

const DemoReport = () => {
    const [demoDetailList, setDemoDetailList] = useState<any[]>([]);
    const [isView, setIsView] = useState(false);
    const [selectedVisit, setSelectedVisit] = useState<VisitData | null>(null);
    const [crops, setCrops] = useState<Array<{ id: number, name: string }>>([]);
    const [products, setProducts] = useState<Array<{ id: number, name: string }>>([]);
    const [isFilterOpened, setIsFilterOpened] = useState(false);
    const [activeFilterParams, setActiveFilterParams] = useState<Record<string, string>>({});
    const activeFilterParamsRef = useRef<Record<string, string>>({});
    const [pageNumber, setPageNumber] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    const [filterVersion, setFilterVersion] = useState(0);
    const [loading, setLoading] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [activeFilterFormValues, setActiveFilterFormValues] = useState<Partial<FilterFormValues>>({});
    const [filterChips, setFilterChips] = useState<FilterChip[]>([]);

    const getDemoDetails = async (overrideParams?: Record<string, string>) => {
        const baseParams: Record<string, string> = overrideParams
            ? { ...overrideParams, page: "1", size: String(itemsPerPage) }
            : (() => {
                const p: Record<string, string> = Object.keys(activeFilterParams).length > 0
                    ? { ...activeFilterParams }
                    : (() => {
                        const { fromDate, toDate } = getDefaultDateRange();
                        return { fromDate, toDate };
                    })();
                p.page = String(pageNumber);
                p.size = String(itemsPerPage);
                return p;
            })();
        setLoading(true);

        await httpClient.get(`/product-demo/complete/team?${new URLSearchParams(baseParams).toString()}`)
            .then((response: any) => {
                console.log("Get demo response: ", response);
                if (response.success) {
                    setDemoDetailList(response.value.contents ?? []);
                    setTotalItems(response.value.totalElements ?? 0);
                }
            })
            .catch((error) => {
                console.error("This is getting demo list error: ", error);
            }).finally(() => {
                setLoading(false);
            })
    }

    const fetchFilterData = () => {
        // Fetch crops data
        httpClient
            .get("crop/all?page=1&size=2000")
            .then((res: any) => {
                console.log("Crops data for filter:", res);
                if (res.value.contents && Array.isArray(res.value.contents)) {
                    setCrops(res.value.contents.map((crop: any) => ({
                        id: crop.id,
                        name: crop.cropName
                    })));
                }
            })
            .catch((error) => {
                console.error("Error fetching crops data:", error);
            });

        // Fetch products data
        httpClient
            .get("product/all?page=1&size=2000")
            .then((res: any) => {
                console.log("Products data for filter:", res);
                if (res.value.contents && Array.isArray(res.value.contents)) {
                    setProducts(res.value.contents.map((product: any) => ({
                        id: product.id,
                        name: product.productName
                    })));
                }
            })
            .catch((error) => {
                console.error("Error fetching products data:", error);
            });
    };

    useEffect(() => {
        fetchFilterData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        getDemoDetails();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageNumber, itemsPerPage, filterVersion]);

    const handleView = useCallback((row: VisitData) => {
        console.log("Handle View data: ", row);
        if (!row) {
            console.error("Attempted to view details for undefined row");
            return;
        }
        setIsView(true);
        setSelectedVisit(row);
    }, []);
    const handleDownload = useCallback((row: VisitData) => {
        if (!row) {
            console.error("Attempted to download details for undefined row");
            return;
        }
        const token = localStorage.getItem("accessToken");
        const baseUrl = appConfig.getBaseUrl();
        const date = new Date().toLocaleDateString();
        console.log("Date in local string:", date);

        axios.get(`${baseUrl}/reports/demo/${row.id}/pdf`, {
            responseType: "blob",
            headers: { Authorization: `Bearer ${token}` },
        })
            .then((response) => {
                const url = window.URL.createObjectURL(
                    new Blob([response.data], { type: "application/pdf" })
                );
                console.log("Demo pdf download name: ", response);
                const disposition = response.headers["content-disposition"];
                console.log("This is the vist excell response: ", response);
                console.log("THis is the Visit excell export disposition : ", disposition);
                const fileName = getFileNameFromDisposition(disposition);
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", fileName);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            })
            .catch((error) => {
                console.error("Download PDF error: ", error);
            });
    }, []);


    const sortedData = useMemo(() =>
        demoDetailList.map(item => ({ ...item, _originalData: item })),
        [demoDetailList]
    );
    const hasFollowUp = sortedData?.some(
        (row: any) =>
            row?.nextFollowUpDate &&
            row?.nextFollowUpDate !== 0
    );

    const renderActions = useCallback((row: any) => <ActionsCell row={row} onView={handleView} onDownload={handleDownload} />, [handleView, handleDownload]);

    const demoColumns = [
        {
            key: "managerName",
            label: "Manager Name",
            render: (_: any, row: any) => (
                <div className="flex flex-row item-center">
                    <div>
                        <div className="font-medium text-sm text-gray-800 dark:text-gray-200">{row.managerName || 'Direct'}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                            {(row.managerCode !== "N/A") && row.managerCode}
                        </div>
                    </div>
                </div>
                // <span>{row.managerCode && row.managerCode !== "N/A" ? `${row.managerCode}/${row.managerName}` : (row.managerName ?? "—")}</span>
            ),
        },
        {
            key: "employeeName",
            label: "Employee Name",
            render: (_: any, row: any) => (
                <div className="flex flex-row item-center">
                    <div>
                        <div className="font-medium text-gray-800 dark:text-gray-200">{row.employeeName || 'Direct'}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                            {row.employeeCode && row.employeeCode}
                        </div>
                    </div>
                </div>
            ),
        },
        {
            key: "farmerName",
            label: "Farmer Name",
            render: (value: ReactNode, row: any) => (
                <div className="flex items-center">

                    <div>
                        <div className="font-medium text-gray-800 dark:text-gray-200">{value || 'N/A'}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                            {row?._originalData?.farmerContactNumber || 'No Contact'}
                        </div>
                    </div>
                </div>
            )
        },
        {
            key: "cropName",
            label: "Crop",
            render: (value: ReactNode) => (
                <div className="flex items-center">

                    <span className="text-gray-700 dark:text-gray-300">{value}</span>
                </div>
            )
        },
        {
            key: "cultivatedAcres",
            label: "Acres",
            render: (value: ReactNode) => (
                <div className="bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 py-1 px-2 rounded-md text-center font-medium text-sm">
                    {value}
                </div>
            )
        },
        {
            key: "productNames",
            label: "Products",
            render: (value: ReactNode) => {
                const products = value as Array<{ id: number; name: string }>;
                if (!products || products.length === 0) {
                    return (
                        <div className="flex items-center">

                            <span className="text-gray-500 dark:text-gray-400 italic">No products</span>
                        </div>
                    );
                }

                if (products.length === 1) {
                    return (
                        <div className="flex items-center">

                            <span className="text-gray-700 dark:text-gray-300">{products[0].name}</span>
                        </div>
                    );
                }

                return (
                    <div className="flex items-start">

                        <div className="flex flex-col gap-1">
                            {products.map((product, index) => (
                                <span
                                    key={product.id || index}
                                    className="text-gray-700 dark:text-gray-300 text-sm  px-2 py-0.5 rounded inline-block"
                                >
                                    {product.name}
                                </span>
                            ))}
                        </div>
                    </div>
                );
            }
        },
        {
            key: "purchaseQuantity",
            label: "Purchase Qty",
            render: (value: ReactNode) => (
                <div className="bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 py-1 px-2 rounded-md text-center font-medium text-sm">
                    {value}
                </div>
            )
        },
        {
            key: "createdDate",
            label: "Created",
            render: (value: ReactNode) => (
                <DateTimeCell value={value as number} />
            )
        },
        {
            key: "completedDate",
            label: "Completed",
            render: (value: ReactNode, row: any) => (
                <DateTimeCell value={value as number} row={row} />
            )
        },
        //  if there is no follow-up date column will not display
        ...(
            hasFollowUp ? [
                {
                    key: "nextFollowUpDate",
                    label: "Follow Up",
                    render: (value: ReactNode, row: any) => (
                        <FollowUpDateTimeCell value={value as number} />
                    )
                },
            ]
                : []),

        {
            key: "actions",
            label: "Actions",
            excludeExport: true,
            actions: renderActions,
        },
    ];

    const handleFilterClose = () => {
        setIsFilterOpened(false)
    }

    const onSubmit = (data: TotalSummaryFormProps) => {
        console.log("Applied Filter data: ", data);

        // Build params — only include fields the user actually filled in
        const params: Record<string, string> = {};
        if (data.fromDate) params.fromDate = `${data.fromDate}`;
        if (data.toDate) params.toDate = `${data.toDate}`;
        if (data.manager && data.manager !== "All") params.managerId = data.manager;
        if (data.employee && data.employee !== "All") params.empIds = data.employee;
        if ((data as any).product && (data as any).product !== "All") params.productId = (data as any).product;
        if ((data as any).crop && (data as any).crop !== "All") params.cropId = (data as any).crop;
        if ((data as any).status === 0 || (data as any).status === 1 && (data as any).status !== "All") params.status = (data as any).status;
        if ((data as any).distributor && (data as any).distributor !== "All") params.distributor = (data as any).distributor;

        console.log("Applied Filter data: ", params);
        // If no dates provided by user, fall back to default range
        if (!params.fromDate && !params.toDate) {
            const { fromDate, toDate } = getDefaultDateRange();
            params.fromDate = fromDate;
            params.toDate = toDate;
        }

        // Store for reuse (e.g. Download button)
        activeFilterParamsRef.current = params;
        setActiveFilterParams(params);
        setActiveFilterFormValues({
            ...data,
            fromDate: msToDateInput(params.fromDate),
            toDate: msToDateInput(params.toDate)
        });

        // Build filter chips for display
        const chips: FilterChip[] = [];
        if (params.fromDate || params.toDate) {
            const from = msToDateInput(params.fromDate);
            const to = msToDateInput(params.toDate);
            chips.push({
                id: "dateRange",
                label: `Date: ${from} – ${to}`,
                onRemove: () => removeChip("dateRange", ["fromDate", "toDate"], ["fromDate", "toDate"]),
            });
        }
        if (params.managerId) {
            chips.push({
                id: "manager",
                label: `Manager: ${(data as any).__managerLabel ?? params.managerId}`,
                onRemove: () => removeChip("manager", ["managerId"], ["manager"]),
            });
        }
        if (params.empIds) {
            chips.push({
                id: "employee",
                label: `Employee: ${(data as any).__employeeLabel ?? params.empIds}`,
                onRemove: () => removeChip("employee", ["empIds"], ["employee"]),
            });
        }
        if (params.productId) {
            chips.push({
                id: "product",
                label: `Product: ${(data as any).__productLabel ?? params.productId}`,
                onRemove: () => removeChip("product", ["productId"], ["product"]),
            });
        }
        if (params.cropId) {
            chips.push({
                id: "crop",
                label: `Crop: ${(data as any).__cropLabel ?? params.cropId}`,
                onRemove: () => removeChip("crop", ["cropId"], ["crop"]),
            });
        }
        if (params.status !== undefined) {
            chips.push({
                id: "status",
                label: `Status: ${(data as any).__statusLabel ?? params.status}`,
                onRemove: () => removeChip("status", ["status"], ["status"]),
            });
        }
        setFilterChips(chips);

        setPageNumber(1);
        setFilterVersion(v => v + 1);
        setIsFilterOpened(false);
    };

    const handlePageChange = (newPage: number) => {
        setPageNumber(newPage);
    };

    const handleItemsPerPageChange = (newItemsPerPage: number) => {
        setItemsPerPage(newItemsPerPage);
        setPageNumber(1);
    };

    const handleClearFilter = () => {
        activeFilterParamsRef.current = {};
        setActiveFilterParams({});
        setActiveFilterFormValues({});
        setFilterChips([]);
        setPageNumber(1);
        setFilterVersion(v => v + 1);
    };

    const removeChip = (chipId: string, paramKeys: string[], formKeys: string[]) => {
        // Read from ref — always current, even when called from a stale closure
        const newParams = { ...activeFilterParamsRef.current };
        if (chipId === "dateRange") {
            // Replace with default month dates instead of removing dates entirely
            const { fromDate, toDate } = getDefaultDateRange();
            newParams.fromDate = fromDate;
            newParams.toDate = toDate;
        } else {
            paramKeys.forEach(k => delete newParams[k]);
        }
        setFilterChips(prev => prev.filter(c => c.id !== chipId));
        activeFilterParamsRef.current = newParams;
        setActiveFilterParams(newParams);
        setActiveFilterFormValues(prev => {
            const next = { ...prev } as any;
            formKeys.forEach(k => delete next[k]);
            // If fromDate/toDate present in newParams, convert to date string
            if (newParams.fromDate) next.fromDate = msToDateInput(newParams.fromDate);
            if (newParams.toDate) next.toDate = msToDateInput(newParams.toDate);
            return next;
        });
        setPageNumber(1);
        // Call directly with computed params to bypass stale closure
        getDemoDetails(newParams);
    };


    const handleDownloadExcel = async () => {
        const token = localStorage.getItem("accessToken");
        const baseUrl = appConfig.getBaseUrl();
        console.log("Base URl: ", baseUrl);
        // Use active filter params if the user applied a filter, otherwise fall back to default date range
        const params: Record<string, string> = Object.keys(activeFilterParams).length > 0
            ? { ...activeFilterParams }
            : (() => {
                const { fromDate, toDate } = getDefaultDateRange();
                return { fromDate, toDate };
            })();

        setIsDownloading(true);

        await axios.get(
            `${baseUrl}/product-demo/complete/team/export?${new URLSearchParams(params).toString()}`,
            {
                responseType: "blob",
                headers: { Authorization: `Bearer ${token}` },
            }
        )
            .then((response) => {
                const url = window.URL.createObjectURL(
                    new Blob([response.data], {
                        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    })
                );
                console.log("This is the Demo report resposne: ", response);
                const disposition = response.headers["content-disposition"];
                console.log("This is the demo excell file disposition: ", disposition);
                const fileName = getFileNameFromDisposition(disposition);
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", fileName);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            })
            .catch((error) => {
                console.error("Generate Report error: ", error);
            })
            .finally(() => {
                setIsDownloading(false);
            })
    };

    return (
        <div className="flex flex-col w-full">
            <header className="flex w-full flex-row items-center justify-between gap-4 mb-4 px-2 ">
                <section className="item-start w-[75%]">
                    <h2 className="text-gray-800 dark:text-white text-xl font-semibold">Demo List</h2>
                </section>
                <section className="item-end flex flex-row items-center justify-end w-[25%] gap-4">
                    <div className="relative">
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => setIsFilterOpened(!isFilterOpened)}
                            className="relative px-3 py-2 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-800/40 transition-colors duration-200 flex items-center"
                        >
                            <FontAwesomeIcon icon={faFilter} className="mr-1.5" />
                            <span>Filter</span>
                            {filterChips.length > 0 && (
                                <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-blue-600 text-[10px] text-white font-bold">
                                    {filterChips.length}
                                </span>
                            )}
                        </motion.button>

                        {isFilterOpened && (
                            <div
                                style={{
                                    position: 'absolute',
                                    zIndex: 50,
                                    top: '100%',
                                    right: 0,
                                    marginTop: '8px',
                                    width: '400px',
                                    maxWidth: 'calc(100vw - 2rem)'
                                }}
                            >
                                <DemoReportFilter onClose={handleFilterClose} title="Generate Demo Report" onSubmit={onSubmit} onReset={handleClearFilter} showCropField={true} showProductField={true} crops={crops} products={products} showStatus={true} initialValues={activeFilterFormValues} />
                            </div>
                        )}
                    </div>
                    <div>

                        <CustomButton onClick={handleDownloadExcel} loading={isDownloading}> {!isDownloading && <FontAwesomeIcon icon={faDownload} className="mr-1.5" />} Download</CustomButton>
                    </div>
                </section>
            </header>
            <FilterBadge count={filterChips.length} onClear={handleClearFilter} chips={filterChips} />
            <main className="w-full h-full border ">
                <DataTable
                    columns={demoColumns}
                    data={sortedData}
                    isAddBtnTrue={false}
                    isSearchBtn={false}
                    isExport={true}
                    serverSide={true}
                    currentPage={pageNumber}
                    totalItems={totalItems}
                    itemsPerPage={itemsPerPage}
                    onPageChange={handlePageChange}
                    onItemsPerPageChange={handleItemsPerPageChange}
                    loading={loading}
                />
            </main>
            {isView && selectedVisit && (
                <GenericModal
                    isOpen={isView}
                    onClose={() => setIsView(false)}
                    header={`${selectedVisit.farmerName} Details`}
                    size="xl"
                    // customHeight="90vh"
                    customWidth="md:max-w-5xl"
                >
                    <ViewDetails viewDetails={selectedVisit as any}
                        onDeleteSuccess={getDemoDetails} onClose={() => setIsView(false)} />
                </GenericModal>
            )}
        </div>
    )
}

export default DemoReport